<template>
  <v-container fluid>
    <v-card-actions>
      <v-btn-toggle 
        v-model="toggle" 
        mandatory
        group
        color="primary"
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn 
              text 
              icon
              v-on="on"
              @click="showActiveAwards = 1"
            >
              <v-icon>
                select_all
              </v-icon>
            </v-btn>
          </template>
          <span>Active Positions</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn 
              text
              icon 
              v-on="on"
              @click="showActiveAwards = 0"
            >
              <v-icon>delete_outline</v-icon>
            </v-btn>
          </template>
          <span>Trash</span>
        </v-tooltip>
      </v-btn-toggle>      
      <v-divider
        class="mr-5 hidden-sm-and-down"
        vertical
      />
      <div
        v-show="showActiveAwards"
        class="hidden-sm-and-down"
      >
        <setup-awards-dialog-create @created="loadAwards" />
      </div>
      <v-spacer /><v-spacer /><v-spacer /><v-spacer /><v-spacer /><v-spacer />
      <v-text-field
        v-model="search"
        class="hidden-sm-and-down"
        label="Award"
        placeholder="Key in award name here"
        append-icon="search"
      />
    </v-card-actions>
    <v-row>
      <v-col
        v-for="award in filteredAwards"
        :key="award.id"
        cols="12"
        md="6"
        xl="3"
      >
        <v-card class="elevation-5">
          <v-container fluid>
            <v-row>
              <v-col
                class="pt-0"
              >
                <v-card-title 
                  class="justify-center" 
                  primary-title
                >
                  <div>
                    <div 
                      style="max-width: 250px;"
                      class="title text-capitalize text-center text-truncate"
                    >
                      {{ award.name }}
                    </div>
                  </div>
                </v-card-title>
                <helper-image-input  
                  v-model="images[award.id]"              
                  :max-size-in-mb="2"
                  data-vv-name="image"
                  field-name="image"
                  accept-type=".svg"      
                  extension="ext:svg"        
                >  
                  <v-card-title 
                    slot="activator"
                    class="justify-center" 
                    primary-title 
                  >
                    <v-img
                      :src="images[award.id] ? images[award.id].imageURL : award.image_url"
                      class="cursor-pointer"
                      height="100px"
                      contain
                    />         
                  </v-card-title>                            
                </helper-image-input>
                <div 
                  v-if="images[award.id]"
                  class="text-center"
                >         
                  <v-btn 
                    :disabled="saving"
                    text
                    icon
                    @click="resetImageHolder(award.id)"
                  >
                    <v-icon size="30">
                      mdi-close-circle-outline
                    </v-icon>
                  </v-btn>     
                  <v-btn 
                    :loading="saving"
                    text
                    icon
                    color="primary"
                    @click="uploadImage(award.id)"
                  >
                    <v-icon size="30">
                      mdi-cloud-upload
                    </v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <v-divider light />
          <v-card-actions v-if="showActiveAwards">
            <div>
              <setup-awards-dialog-edit
                :award="award"
                @updated="refreshData"
              />
              <setup-awards-dialog-delete
                :award="award"
                @deleted="refreshData"
              />    
            </div>             
            <v-spacer />
          </v-card-actions>
          <v-card-actions v-else>
            <v-spacer />
            <setup-awards-dialog-restore
              :award="award"
              @restored="refreshData"
            />              
            <v-spacer />
          </v-card-actions>
        </v-card>        
      </v-col>
    </v-row> 
    <v-row
      v-show="filteredAwards.length === 0 && loading === false"
      justify="center"
    >
      <v-col
        class="text-center"
        cols="12"
      >
        <div class="headline">
          <span class="blue-grey--text">No Data Available</span>
        </div>
      </v-col>   
    </v-row>
  </v-container>
</template>

<script>
  export default {
    components: {
      SetupAwardsDialogCreate: () => import('@/components/setup/awards/DialogCreate'),
      HelperImageInput: () => import('@/components/helper/ImageInput'),
      SetupAwardsDialogEdit: () => import('@/components/setup/awards/DialogEdit'),
      SetupAwardsDialogDelete: () => import('@/components/setup/awards/DialogDelete'),
      SetupAwardsDialogRestore: () => import('@/components/setup/awards/DialogRestore'),
    },
    metaInfo () {
      return {
        title: 'Awards',
      }
    },
    data () {
      return {
        max250chars: v => v.length <= 250 || 'Input too long!',
        awards: [],
        images: [],
        loading: true,
        saving: false,
        showActiveAwards: 1,
        shows: [],
        search: '',
        toggle: 0,
        trashedAwards: [],
      }
    },
    computed: {
      filteredAwards () {
        let awards = this.showActiveAwards ? this.awards : this.trashedAwards
        return awards.filter(award => {
          return award.name.toLowerCase().indexOf(this.search.toLowerCase()) >= 0
        })
      },
    },
    watch: {
      showActiveAwards: {
        immediate: true,
        handler (value) {
          if (value) {
            if (this.awards.length <= 0) this.loadAwards()
          } else {
            if (this.trashedAwards.length <= 0) this.loadTrashedAwards()
          }
        },
      },
    },
    created () {   
      this.refreshData()
    },
    methods: {
      loadAwards () {
        this.$api.awards().list()
          .then(response => {
            this.awards = response.data
            this.loading = false
          })      
      },
      loadTrashedAwards () {
        this.$api.awards().listTrashed()
          .then(response => {
            this.trashedAwards = response.data
            this.loading = false
          })
      },
      resetImageHolder (id) {
        this.$set(this.images, id, null)
      },
      uploadImage (id) {
        this.saving = true

        this.$api.awards().uploadImage(id, this.images[id].formData)
          .then(response => {
            if (response.status === 200) {
              this.savedImage(id)
            }
          })
          .catch(() => {
            this.$snack({
              color: 'error',
              message: 'Photo upload failed, please try again',
            })
          }) 
          .then(() => {
            this.saving = false
          })
      },

      onClickHandler (id) {
        this.$set(this.shows, id, !this.shows[id])
      },
      savedImage (id) {     
        this.$api.awards().list()
          .then(response => {
            this.awards = response.data

            this.images[id] = null
          })      

        this.$snack({
          message: 'Photo uploaded successfully!',
        })
      },
      refreshData () {
        this.loadAwards()
        this.loadTrashedAwards()
      },
    },
  }
</script>

<style scoped>
   ::v-deep .v-toolbar__content{
    margin-left:0px !important;
  }

  .cursor-pointer{
    cursor: pointer
  }
</style>
